'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  applyMp3Fade,
  convertAacToMp3,
  changeMp3Speed,
  changeMp3Volume,
  convertAudioToFormat,
  convertAudioToMp3,
  convertM4aToMp3,
  convertMp3ToWav,
  convertWavToMp3,
  extractVideoAudioToMp3,
  makeRingtoneMp3,
  trimAudioToMp3,
  trimAudioToOriginalFormat,
  trimMp3,
  type AudioOutputFormat,
} from '@/lib/browser-audio-tools';
import { clamp, formatFileSize, getBaseName } from '@/lib/browser-image-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  acceptedAacMimeTypes,
  acceptedAudioMimeTypes,
  acceptedCuttableAudioMimeTypes,
  acceptedM4aMimeTypes,
  acceptedVideoMimeTypes,
  acceptedVoiceNoteMimeTypes,
  acceptedWavMimeTypes,
  formatBitrateKbps,
  formatDurationMs,
  useLoadedMediaSource,
  type AudioSourceMode,
} from '@/components/tools/audio-tool-shared';

export type AudioSingleToolVariant =
  | 'mp3-trimmer'
  | 'audio-cutter'
  | 'audio-to-mp3-converter'
  | 'audio-converter'
  | 'm4a-to-mp3'
  | 'wav-to-mp3'
  | 'aac-to-mp3'
  | 'flac-to-mp3'
  | 'ogg-to-mp3'
  | 'mp3-volume-changer'
  | 'mp3-speed-changer'
  | 'mp3-to-wav-converter'
  | 'audio-extractor-to-mp3'
  | 'mp3-fade-in-out'
  | 'ringtone-maker'
  | 'voice-note-converter';

type ToolCopy = {
  title: string;
  subtitle: string;
  badge: string;
  inputLabel: string;
  inputHint: string;
  buttonLabel: string;
  processingLabel: string;
  successLabel: string;
  sourceMode: AudioSourceMode;
  accept: string;
  outputExtension: 'mp3' | 'wav' | 'm4a' | 'aac' | 'ogg' | 'flac';
  outputMimeType: string;
};

const bitrateOptions = [128, 192, 256, 320];
const speedPresets = [0.5, 0.75, 1.25, 1.5, 2];
const ringtoneMaxDurationMs = 45_000;

function getOutputBaseName(variant: AudioSingleToolVariant, fileName: string) {
  const baseName = getBaseName(fileName);

  switch (variant) {
    case 'mp3-trimmer':
      return `${baseName}-trimmed`;
    case 'audio-cutter':
      return `${baseName}-cut`;
    case 'audio-to-mp3-converter':
    case 'audio-converter':
      return `${baseName}-converted`;
    case 'm4a-to-mp3':
    case 'wav-to-mp3':
    case 'aac-to-mp3':
    case 'flac-to-mp3':
    case 'ogg-to-mp3':
    case 'voice-note-converter':
      return `${baseName}-mp3`;
    case 'mp3-volume-changer':
      return `${baseName}-volume`;
    case 'mp3-speed-changer':
      return `${baseName}-speed`;
    case 'mp3-to-wav-converter':
      return `${baseName}-wav`;
    case 'audio-extractor-to-mp3':
      return `${baseName}-audio`;
    case 'mp3-fade-in-out':
      return `${baseName}-faded`;
    case 'ringtone-maker':
      return `${baseName}-ringtone`;
  }
}

const toolCopy: Record<AudioSingleToolVariant, ToolCopy> = {
  'mp3-trimmer': {
    title: 'Trim an MP3 by start and end time',
    subtitle: 'Pick the clip range and export a shorter MP3 locally in your browser.',
    badge: 'MP3',
    inputLabel: 'Choose one MP3 or drop it here',
    inputHint:
      'Trim intros, outros, voice notes, and short sections without uploading them anywhere',
    buttonLabel: 'Trim MP3',
    processingLabel: 'Trimming…',
    successLabel: 'MP3 trim',
    sourceMode: 'mp3',
    accept: 'audio/mpeg,.mp3',
    outputExtension: 'mp3',
    outputMimeType: 'audio/mpeg',
  },
  'audio-cutter': {
    title: 'Cut audio files by start and end time',
    subtitle:
      'Trim M4A, MP3, WAV, AAC, or OGG audio locally and export MP3 or the original format.',
    badge: 'Cut',
    inputLabel: 'Choose one audio file or drop it here',
    inputHint: 'Supports M4A, MP3, WAV, AAC, and OGG for quick clip cuts',
    buttonLabel: 'Cut audio',
    processingLabel: 'Cutting…',
    successLabel: 'Audio cut',
    sourceMode: 'audio-cutter',
    accept: acceptedCuttableAudioMimeTypes,
    outputExtension: 'mp3',
    outputMimeType: 'audio/mpeg',
  },
  'audio-to-mp3-converter': {
    title: 'Convert audio files to MP3',
    subtitle: 'Turn WAV, M4A, AAC, OGG, FLAC, or WebM audio into MP3 locally.',
    badge: 'Convert',
    inputLabel: 'Choose one audio file or drop it here',
    inputHint: 'Supports WAV, M4A, AAC, OGG, FLAC, and WebM audio',
    buttonLabel: 'Convert to MP3',
    processingLabel: 'Converting…',
    successLabel: 'Audio-to-MP3 conversion',
    sourceMode: 'audio',
    accept: acceptedAudioMimeTypes,
    outputExtension: 'mp3',
    outputMimeType: 'audio/mpeg',
  },
  'audio-converter': {
    title: 'Convert audio to any format',
    subtitle:
      'Turn WAV, M4A, AAC, OGG, FLAC, or WebM audio into MP3, WAV, M4A, AAC, or OGG locally.',
    badge: 'Convert',
    inputLabel: 'Choose one audio file or drop it here',
    inputHint:
      'Supports WAV, M4A, AAC, OGG, FLAC, and WebM audio in, and MP3, WAV, M4A, AAC, or OGG out',
    buttonLabel: 'Convert audio',
    processingLabel: 'Converting…',
    successLabel: 'Audio conversion',
    sourceMode: 'audio',
    accept: acceptedAudioMimeTypes,
    outputExtension: 'mp3',
    outputMimeType: 'audio/mpeg',
  },
  'm4a-to-mp3': {
    title: 'Convert M4A to MP3',
    subtitle: 'Turn one M4A file into MP3 locally with a simple bitrate choice.',
    badge: 'M4A',
    inputLabel: 'Choose one M4A file or drop it here',
    inputHint: 'Useful for iPhone voice memos, recordings, and browser-safe M4A audio',
    buttonLabel: 'Convert M4A to MP3',
    processingLabel: 'Converting…',
    successLabel: 'M4A-to-MP3 conversion',
    sourceMode: 'm4a',
    accept: acceptedM4aMimeTypes,
    outputExtension: 'mp3',
    outputMimeType: 'audio/mpeg',
  },
  'wav-to-mp3': {
    title: 'Convert WAV to MP3',
    subtitle: 'Shrink a WAV file into a more shareable MP3 locally in your browser.',
    badge: 'WAV',
    inputLabel: 'Choose one WAV file or drop it here',
    inputHint: 'Useful when a large WAV needs a lighter MP3 version for sharing or upload',
    buttonLabel: 'Convert WAV to MP3',
    processingLabel: 'Converting…',
    successLabel: 'WAV-to-MP3 conversion',
    sourceMode: 'wav',
    accept: acceptedWavMimeTypes,
    outputExtension: 'mp3',
    outputMimeType: 'audio/mpeg',
  },
  'aac-to-mp3': {
    title: 'Convert AAC to MP3',
    subtitle: 'Convert one AAC file into MP3 locally with a straightforward export flow.',
    badge: 'AAC',
    inputLabel: 'Choose one AAC file or drop it here',
    inputHint: 'Useful when AAC playback is awkward and you need a more universal MP3 file',
    buttonLabel: 'Convert AAC to MP3',
    processingLabel: 'Converting…',
    successLabel: 'AAC-to-MP3 conversion',
    sourceMode: 'aac',
    accept: acceptedAacMimeTypes,
    outputExtension: 'mp3',
    outputMimeType: 'audio/mpeg',
  },
  'flac-to-mp3': {
    title: 'Convert FLAC to MP3',
    subtitle: 'Turn a lossless FLAC file into a smaller, more shareable MP3 locally.',
    badge: 'FLAC',
    inputLabel: 'Choose one FLAC file or drop it here',
    inputHint: 'Useful when a large lossless FLAC needs a lighter MP3 for phones and players',
    buttonLabel: 'Convert FLAC to MP3',
    processingLabel: 'Converting…',
    successLabel: 'FLAC-to-MP3 conversion',
    sourceMode: 'audio',
    accept: 'audio/flac,audio/x-flac,.flac',
    outputExtension: 'mp3',
    outputMimeType: 'audio/mpeg',
  },
  'ogg-to-mp3': {
    title: 'Convert OGG to MP3',
    subtitle: 'Convert one OGG (Vorbis/Opus) file into MP3 locally in your browser.',
    badge: 'OGG',
    inputLabel: 'Choose one OGG file or drop it here',
    inputHint: 'Useful when an OGG file will not play in an app that expects MP3',
    buttonLabel: 'Convert OGG to MP3',
    processingLabel: 'Converting…',
    successLabel: 'OGG-to-MP3 conversion',
    sourceMode: 'audio',
    accept: 'audio/ogg,application/ogg,.ogg,.oga',
    outputExtension: 'mp3',
    outputMimeType: 'audio/mpeg',
  },
  'mp3-volume-changer': {
    title: 'Change MP3 volume locally',
    subtitle: 'Increase or reduce MP3 loudness and export a fresh MP3 in the browser.',
    badge: 'Volume',
    inputLabel: 'Choose one MP3 or drop it here',
    inputHint: 'Useful for quiet lectures, podcasts, and spoken audio clips',
    buttonLabel: 'Change volume',
    processingLabel: 'Applying volume…',
    successLabel: 'MP3 volume change',
    sourceMode: 'mp3',
    accept: 'audio/mpeg,.mp3',
    outputExtension: 'mp3',
    outputMimeType: 'audio/mpeg',
  },
  'mp3-speed-changer': {
    title: 'Change MP3 playback speed',
    subtitle: 'Speed up or slow down spoken audio while keeping the workflow browser-first.',
    badge: 'Speed',
    inputLabel: 'Choose one MP3 or drop it here',
    inputHint: 'Useful for lectures, voice notes, podcasts, and listening practice',
    buttonLabel: 'Change speed',
    processingLabel: 'Changing speed…',
    successLabel: 'MP3 speed change',
    sourceMode: 'mp3',
    accept: 'audio/mpeg,.mp3',
    outputExtension: 'mp3',
    outputMimeType: 'audio/mpeg',
  },
  'mp3-to-wav-converter': {
    title: 'Convert MP3 to WAV',
    subtitle: 'Turn an MP3 into a WAV export for editing, import, or archive workflows.',
    badge: 'WAV',
    inputLabel: 'Choose one MP3 or drop it here',
    inputHint: 'Export a local WAV without sending the MP3 to a server',
    buttonLabel: 'Convert to WAV',
    processingLabel: 'Converting…',
    successLabel: 'MP3-to-WAV conversion',
    sourceMode: 'mp3',
    accept: 'audio/mpeg,.mp3',
    outputExtension: 'wav',
    outputMimeType: 'audio/wav',
  },
  'audio-extractor-to-mp3': {
    title: 'Extract audio from video as MP3',
    subtitle: 'Load a video, choose the clip range, and export audio-only MP3 locally.',
    badge: 'Extract',
    inputLabel: 'Choose one video file or drop it here',
    inputHint: 'Works best for short clips where you only need the audio track',
    buttonLabel: 'Extract MP3',
    processingLabel: 'Extracting…',
    successLabel: 'Audio extraction',
    sourceMode: 'video',
    accept: acceptedVideoMimeTypes,
    outputExtension: 'mp3',
    outputMimeType: 'audio/mpeg',
  },
  'mp3-fade-in-out': {
    title: 'Add fade in and fade out to MP3',
    subtitle: 'Apply simple fades to intros, outros, ringtone clips, and short spoken audio.',
    badge: 'Fade',
    inputLabel: 'Choose one MP3 or drop it here',
    inputHint: 'Apply fades locally and export a new MP3 right away',
    buttonLabel: 'Apply fades',
    processingLabel: 'Applying fades…',
    successLabel: 'MP3 fade export',
    sourceMode: 'mp3',
    accept: 'audio/mpeg,.mp3',
    outputExtension: 'mp3',
    outputMimeType: 'audio/mpeg',
  },
  'ringtone-maker': {
    title: 'Make a ringtone from an audio clip',
    subtitle:
      'Cut a short clip, add fades, adjust loudness, and export a ringtone-ready MP3 locally.',
    badge: 'RING',
    inputLabel: 'Choose one audio file or drop it here',
    inputHint: 'Supports M4A, MP3, WAV, AAC, and OGG audio for short ringtone clips',
    buttonLabel: 'Make ringtone',
    processingLabel: 'Making ringtone…',
    successLabel: 'Ringtone export',
    sourceMode: 'audio-cutter',
    accept: acceptedCuttableAudioMimeTypes,
    outputExtension: 'mp3',
    outputMimeType: 'audio/mpeg',
  },
  'voice-note-converter': {
    title: 'Convert voice notes to MP3',
    subtitle:
      'Turn iPhone voice memos, WhatsApp audio, recordings, and browser notes into MP3 locally.',
    badge: 'VOICE',
    inputLabel: 'Choose one voice note or drop it here',
    inputHint:
      'Supports OPUS and AMR from WhatsApp and Android, M4A and CAF from iPhone Voice Memos, plus AAC, OGG, 3GP, WMA, WebM, MP3 and WAV',
    buttonLabel: 'Convert voice note',
    processingLabel: 'Converting…',
    successLabel: 'Voice-note conversion',
    sourceMode: 'voice-note',
    accept: acceptedVoiceNoteMimeTypes,
    outputExtension: 'mp3',
    outputMimeType: 'audio/mpeg',
  },
};

function formatGainDb(gainPercent: number) {
  const multiplier = Math.max(0.05, gainPercent / 100);
  return 20 * Math.log10(multiplier);
}

export type AudioPair = { from: string; to: AudioOutputFormat };

export default function BrowserAudioSingleFileTool({
  variant,
  pair,
  toolSlug,
}: {
  variant: AudioSingleToolVariant;
  /** Format-pair pages (m4a-to-wav …) reuse the converter with their own heading and default. */
  pair?: AudioPair;
  toolSlug?: string;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const from = pair?.from.toUpperCase();
  const to = pair?.to.toUpperCase();
  const copy: ToolCopy = pair
    ? {
        ...toolCopy['audio-converter'],
        title: `Convert ${from} to ${to}`,
        subtitle: `Turn a ${from} file into ${to} right in your browser. Nothing is uploaded.`,
        badge: from ?? 'Audio',
        inputLabel: `Choose a ${from} file or drop it here`,
        inputHint: `${from} in, ${to} out. Other formats work too.`,
        buttonLabel: `Convert to ${to}`,
        accept: `${toolCopy['audio-converter'].accept},.${pair.from}`,
      }
    : toolCopy[variant];
  const { source, loadSource, clearSource } = useLoadedMediaSource(copy.sourceMode);
  const [outputUrl, setOutputUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [bitrateKbps, setBitrateKbps] = useState(192);
  const [startMs, setStartMs] = useState(0);
  const [endMs, setEndMs] = useState(0);
  const [gainPercent, setGainPercent] = useState(100);
  const [speedFactor, setSpeedFactor] = useState(1.25);
  const [customSpeed, setCustomSpeed] = useState('1.25');
  const [fadeInMs, setFadeInMs] = useState(800);
  const [fadeOutMs, setFadeOutMs] = useState(800);
  const [audioCutterOutputMode, setAudioCutterOutputMode] = useState<'mp3' | 'original'>('mp3');
  const [outputFormat, setOutputFormat] = useState<AudioOutputFormat>(pair?.to ?? 'mp3');

  useEffect(() => {
    return () => {
      if (outputUrl) {
        URL.revokeObjectURL(outputUrl);
      }
    };
  }, [outputUrl]);

  useEffect(() => {
    if (!source) {
      return;
    }

    setStartMs(0);
    setEndMs(source.metadata.durationMs);
    setFadeInMs(Math.min(800, Math.max(0, Math.floor(source.metadata.durationMs / 4))));
    setFadeOutMs(Math.min(800, Math.max(0, Math.floor(source.metadata.durationMs / 4))));
  }, [source]);

  function resetOutputState() {
    if (outputUrl) {
      URL.revokeObjectURL(outputUrl);
    }

    setOutputUrl(null);
    setDownloadName(null);
    setResultSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    if (variant === 'audio-to-mp3-converter' && file.type === 'audio/mpeg') {
      clearSource();
      setErrorMessage(
        'This file is already MP3. Use MP3 tools for trimming, volume, speed, split, or fades.',
      );
      return;
    }

    try {
      await loadSource(file);
    } catch (error) {
      clearSource();
      setErrorMessage(
        error instanceof Error ? error.message : 'The source file could not be loaded.',
      );
    }
  }

  async function runTool() {
    if (!source) {
      setErrorMessage(`Choose a ${copy.sourceMode === 'video' ? 'video' : 'source file'} first.`);
      return;
    }

    if (
      (variant === 'mp3-trimmer' ||
        variant === 'audio-cutter' ||
        variant === 'audio-extractor-to-mp3' ||
        variant === 'ringtone-maker') &&
      endMs <= startMs
    ) {
      setErrorMessage('The end time must be later than the start time.');
      return;
    }

    if (variant === 'mp3-fade-in-out' && fadeInMs + fadeOutMs >= source.metadata.durationMs) {
      setErrorMessage('Fade lengths must stay shorter than the full MP3 duration.');
      return;
    }

    if (variant === 'ringtone-maker') {
      const clipDurationMs = Math.max(0, endMs - startMs);

      if (clipDurationMs > ringtoneMaxDurationMs) {
        setErrorMessage(
          'Keep the ringtone clip at 45 seconds or less for a practical ringtone export.',
        );
        return;
      }

      if (fadeInMs + fadeOutMs >= clipDurationMs) {
        setErrorMessage('Fade lengths must stay shorter than the ringtone clip.');
        return;
      }
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        toolSlug ?? variant,
        'Credits are not available for another audio export right now.',
      );

      let outputBlob: Blob;
      let outputExtension = copy.outputExtension;

      switch (variant) {
        case 'mp3-trimmer':
          outputBlob = await trimMp3({
            file: source.file,
            startMs,
            endMs,
            bitrateKbps,
          });
          break;
        case 'audio-cutter':
          if (audioCutterOutputMode === 'original') {
            const exportResult = await trimAudioToOriginalFormat({
              file: source.file,
              startMs,
              endMs,
              bitrateKbps,
            });
            outputBlob = exportResult.blob;
            outputExtension = exportResult.extension;
          } else {
            outputBlob = await trimAudioToMp3({
              file: source.file,
              startMs,
              endMs,
              bitrateKbps,
            });
          }
          break;
        case 'audio-to-mp3-converter':
          outputBlob = await convertAudioToMp3({
            file: source.file,
            bitrateKbps,
          });
          break;
        case 'audio-converter':
          outputBlob = await convertAudioToFormat({
            file: source.file,
            targetFormat: outputFormat,
            bitrateKbps,
          });
          outputExtension = outputFormat;
          break;
        case 'm4a-to-mp3':
          outputBlob = await convertM4aToMp3({
            file: source.file,
            bitrateKbps,
          });
          break;
        case 'wav-to-mp3':
          outputBlob = await convertWavToMp3({
            file: source.file,
            bitrateKbps,
          });
          break;
        case 'aac-to-mp3':
          outputBlob = await convertAacToMp3({
            file: source.file,
            bitrateKbps,
          });
          break;
        case 'flac-to-mp3':
        case 'ogg-to-mp3':
          outputBlob = await convertAudioToMp3({
            file: source.file,
            bitrateKbps,
          });
          break;
        case 'mp3-volume-changer':
          outputBlob = await changeMp3Volume({
            file: source.file,
            gainDb: formatGainDb(gainPercent),
            bitrateKbps,
          });
          break;
        case 'mp3-speed-changer':
          outputBlob = await changeMp3Speed({
            file: source.file,
            speedFactor: clamp(Number(customSpeed) || speedFactor, 0.25, 4),
            bitrateKbps,
          });
          break;
        case 'mp3-to-wav-converter':
          outputBlob = await convertMp3ToWav(source.file);
          break;
        case 'audio-extractor-to-mp3':
          outputBlob = await extractVideoAudioToMp3({
            file: source.file,
            startMs,
            endMs,
            bitrateKbps,
          });
          break;
        case 'mp3-fade-in-out':
          outputBlob = await applyMp3Fade({
            file: source.file,
            durationMs: source.metadata.durationMs,
            fadeInMs,
            fadeOutMs,
            bitrateKbps,
          });
          break;
        case 'ringtone-maker':
          outputBlob = await makeRingtoneMp3({
            file: source.file,
            startMs,
            endMs,
            fadeInMs,
            fadeOutMs,
            gainDb: formatGainDb(gainPercent),
            bitrateKbps,
          });
          break;
        case 'voice-note-converter':
          outputBlob = await convertAudioToMp3({
            file: source.file,
            bitrateKbps,
          });
          break;
      }

      resetOutputState();
      const nextUrl = URL.createObjectURL(outputBlob);
      setOutputUrl(nextUrl);
      setDownloadName(`${getOutputBaseName(variant, source.file.name)}.${outputExtension}`);
      setResultSize(outputBlob.size);
      setCreditMessage(formatToolCreditSuccess(copy.successLabel, credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The audio export could not finish.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  const showRangeControls =
    variant === 'mp3-trimmer' ||
    variant === 'audio-cutter' ||
    variant === 'audio-extractor-to-mp3' ||
    variant === 'ringtone-maker';
  const showBitrateControls =
    variant !== 'mp3-to-wav-converter' &&
    !(variant === 'audio-converter' && (outputFormat === 'wav' || outputFormat === 'flac'));
  const outputDurationLabel = useMemo(() => {
    if (!source) {
      return 'No source loaded';
    }

    if (showRangeControls) {
      return formatDurationMs(Math.max(0, endMs - startMs));
    }

    if (variant === 'mp3-speed-changer') {
      return formatDurationMs(
        Math.round(source.metadata.durationMs / clamp(Number(customSpeed) || speedFactor, 0.25, 4)),
      );
    }

    return formatDurationMs(source.metadata.durationMs);
  }, [customSpeed, endMs, showRangeControls, source, speedFactor, startMs, variant]);

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{copy.title}</h2>
          <p>{copy.subtitle}</p>
        </div>
        <span className="tool-live-badge">{copy.badge}</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.file.name : copy.inputLabel}</strong>
              <span>
                {source
                  ? `${formatFileSize(source.file.size)} • ${formatDurationMs(source.metadata.durationMs)}`
                  : copy.inputHint}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept={copy.accept}
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          {showRangeControls && source ? (
            <div className="tool-live-controls">
              <label className="tool-live-field tool-live-field-full">
                <span>Start</span>
                <input
                  max={Math.max(0, source.metadata.durationMs - 20)}
                  min="0"
                  onChange={(event) => {
                    const nextStart = clamp(
                      Number(event.target.value) || 0,
                      0,
                      source.metadata.durationMs,
                    );
                    setStartMs(nextStart);
                    if (nextStart >= endMs) {
                      setEndMs(Math.min(source.metadata.durationMs, nextStart + 1000));
                    }
                    resetOutputState();
                  }}
                  step="100"
                  type="range"
                  value={startMs}
                />
                <small>{formatDurationMs(startMs)}</small>
              </label>
              <label className="tool-live-field tool-live-field-full">
                <span>End</span>
                <input
                  max={source.metadata.durationMs}
                  min="20"
                  onChange={(event) => {
                    const nextEnd = clamp(
                      Number(event.target.value) || 0,
                      20,
                      source.metadata.durationMs,
                    );
                    setEndMs(nextEnd);
                    if (nextEnd <= startMs) {
                      setStartMs(Math.max(0, nextEnd - 1000));
                    }
                    resetOutputState();
                  }}
                  step="100"
                  type="range"
                  value={endMs}
                />
                <small>{formatDurationMs(endMs)}</small>
              </label>
            </div>
          ) : null}

          {variant === 'audio-cutter' ? (
            <div className="tool-live-controls">
              <label className="tool-live-field">
                <span>Output format</span>
                <select
                  onChange={(event) => {
                    setAudioCutterOutputMode(
                      event.target.value === 'original' ? 'original' : 'mp3',
                    );
                    resetOutputState();
                  }}
                  value={audioCutterOutputMode}
                >
                  <option value="mp3">MP3</option>
                  <option value="original">Original format</option>
                </select>
              </label>
            </div>
          ) : null}

          {variant === 'audio-converter' ? (
            <div className="tool-live-controls">
              <label className="tool-live-field">
                <span>Output format</span>
                <select
                  onChange={(event) => {
                    setOutputFormat(event.target.value as AudioOutputFormat);
                    resetOutputState();
                  }}
                  value={outputFormat}
                >
                  <option value="mp3">MP3</option>
                  <option value="wav">WAV</option>
                  <option value="m4a">M4A</option>
                  <option value="aac">AAC</option>
                  <option value="ogg">OGG</option>
                  <option value="flac">FLAC</option>
                </select>
              </label>
            </div>
          ) : null}

          {variant === 'mp3-volume-changer' || variant === 'ringtone-maker' ? (
            <div className="tool-live-controls">
              <label className="tool-live-field tool-live-field-full">
                <span>{variant === 'ringtone-maker' ? 'Loudness' : 'Volume'}</span>
                <input
                  max="250"
                  min="25"
                  onChange={(event) => {
                    setGainPercent(clamp(Number(event.target.value) || 100, 25, 250));
                    resetOutputState();
                  }}
                  step="5"
                  type="range"
                  value={gainPercent}
                />
                <small>{`${gainPercent}% • ${formatGainDb(gainPercent).toFixed(1)} dB`}</small>
              </label>
            </div>
          ) : null}

          {variant === 'mp3-speed-changer' ? (
            <>
              <div className="tool-live-routes" aria-label="Speed presets">
                {speedPresets.map((preset) => (
                  <button
                    className={`tool-live-route ${Number(customSpeed) === preset ? 'tool-live-route-active' : ''}`}
                    key={preset}
                    onClick={() => {
                      setSpeedFactor(preset);
                      setCustomSpeed(String(preset));
                      resetOutputState();
                    }}
                    type="button"
                  >
                    {preset}x
                  </button>
                ))}
              </div>
              <div className="tool-live-controls">
                <label className="tool-live-field">
                  <span>Custom multiplier</span>
                  <input
                    max="4"
                    min="0.25"
                    onChange={(event) => {
                      setCustomSpeed(event.target.value);
                      resetOutputState();
                    }}
                    step="0.05"
                    type="number"
                    value={customSpeed}
                  />
                </label>
              </div>
            </>
          ) : null}

          {(variant === 'mp3-fade-in-out' || variant === 'ringtone-maker') && source ? (
            <div className="tool-live-controls">
              <label className="tool-live-field">
                <span>Fade in</span>
                <input
                  max={Math.max(
                    0,
                    Math.floor(
                      (variant === 'ringtone-maker'
                        ? Math.max(0, endMs - startMs)
                        : source.metadata.durationMs) / 2,
                    ),
                  )}
                  min="0"
                  onChange={(event) => {
                    setFadeInMs(
                      clamp(Number(event.target.value) || 0, 0, source.metadata.durationMs),
                    );
                    resetOutputState();
                  }}
                  step="100"
                  type="number"
                  value={fadeInMs}
                />
              </label>
              <label className="tool-live-field">
                <span>Fade out</span>
                <input
                  max={Math.max(
                    0,
                    Math.floor(
                      (variant === 'ringtone-maker'
                        ? Math.max(0, endMs - startMs)
                        : source.metadata.durationMs) / 2,
                    ),
                  )}
                  min="0"
                  onChange={(event) => {
                    setFadeOutMs(
                      clamp(Number(event.target.value) || 0, 0, source.metadata.durationMs),
                    );
                    resetOutputState();
                  }}
                  step="100"
                  type="number"
                  value={fadeOutMs}
                />
              </label>
            </div>
          ) : null}

          {showBitrateControls ? (
            <div className="tool-live-controls">
              <label className="tool-live-field">
                <span>Bitrate</span>
                <select
                  onChange={(event) => {
                    setBitrateKbps(Number(event.target.value));
                    resetOutputState();
                  }}
                  value={bitrateKbps}
                >
                  {bitrateOptions.map((option) => (
                    <option key={option} value={option}>
                      {option} kbps
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing}
              onClick={() => void runTool()}
              type="button"
            >
              {isProcessing ? copy.processingLabel : copy.buttonLabel}
            </button>
            {outputUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={outputUrl}>
                Download{' '}
                {downloadName.split('.').pop()?.toUpperCase() ?? copy.outputExtension.toUpperCase()}
              </a>
            ) : null}
          </div>

          {errorMessage ? <p className="tool-live-error">{errorMessage}</p> : null}
          {creditMessage ? <p className="tool-live-success">{creditMessage}</p> : null}
        </div>

        <aside className="tool-live-preview">
          <div className="tool-live-preview-frame">
            {outputUrl ? (
              <audio controls src={outputUrl} />
            ) : source ? (
              source.kind === 'video' ? (
                <video controls playsInline preload="metadata" src={source.previewUrl} />
              ) : (
                <audio controls src={source.previewUrl} />
              )
            ) : (
              <div className="tool-live-panel-note">
                <strong>No source loaded</strong>
                <span>Add one file to preview the source or resulting export.</span>
              </div>
            )}
          </div>

          <div className="tool-live-stats">
            <div>
              <strong>Source</strong>
              <span>{source ? source.file.name : 'No file loaded'}</span>
            </div>
            <div>
              <strong>Duration</strong>
              <span>
                {source ? formatDurationMs(source.metadata.durationMs) : 'No file loaded'}
              </span>
            </div>
            <div>
              <strong>Approx bitrate</strong>
              <span>
                {source
                  ? formatBitrateKbps(source.metadata.approximateBitrateKbps)
                  : 'No file loaded'}
              </span>
            </div>
            <div>
              <strong>Output</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'Run the tool'}</span>
            </div>
            <div>
              <strong>Result duration</strong>
              <span>{source ? outputDurationLabel : 'Run the tool'}</span>
            </div>
            <div>
              <strong>Privacy</strong>
              <span>Processed in your browser</span>
            </div>
          </div>

          {(variant === 'mp3-volume-changer' || variant === 'ringtone-maker') &&
          gainPercent > 170 ? (
            <div className="tool-live-panel-note">
              <strong>Possible clipping</strong>
              <span>
                Large gain boosts can distort the result if the source audio is already loud.
              </span>
            </div>
          ) : null}
          {variant === 'ringtone-maker' ? (
            <div className="tool-live-panel-note">
              <strong>Ringtone-ready clip</strong>
              <span>
                Keep the selected clip short. This tool is tuned for ringtone-style exports, not
                full-track editing.
              </span>
            </div>
          ) : null}
        </aside>
      </div>
    </article>
  );
}
